/**
 * @fileoverview A campaign's overbooking settings, on the campaign edit page.
 *
 * Arthur's rule from the 3 November call: overbooking is a property of the
 * campaign. Ops set the standby share of each shift here, and whether a BA
 * booking a standby seat is told so, with the chance of working the notice
 * shows. Saving applies the share to every shift of the campaign's plans.
 *
 * Kept out of the main campaign form on purpose: it saves on its own, through
 * its own mutation, so it can never hold up or break a campaign save.
 */
import React, { useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import { readGraphQLData } from 'Components/PowerBanks/hooks'
import SubmitButton from 'Components/Form/SubmitButton'
import { SnackBarContext } from 'Components/SnackBar'
import {
  fetchCampaignStandbySettings,
  updateCampaignStandbySettings,
} from 'Module/graphQL'

/** What the notice says until ops pick their own number. */
const DEFAULT_WORK_CHANCE = 96

/** Two standby BAs per van of ten. */
const DEFAULT_PERCENT = 20

/** Arthur's half shift: one hour on NETO, half the shift on the bill. */
const DEFAULT_RELEASE_HOURS = 1
const DEFAULT_RELEASE_PERCENT = 50

/**
 * The app's own wording, shown as placeholders. {chance} becomes the chance
 * of working. A line left empty keeps the app's wording.
 */
const DEFAULT_TEXTS = {
  en: {
    title: 'Standby shift',
    body: 'You have a {chance}% chance of working this shift.',
  },
  he: {
    title: 'משמרת כוננות',
    body: 'יש לך {chance}% סיכוי לעבוד במשמרת הזו.',
  },
}

const LANGUAGES = [
  { code: 'en', name: 'English', dir: 'ltr' },
  { code: 'he', name: 'Hebrew', dir: 'rtl' },
]

const EMPTY_TEXTS = { en: { title: '', body: '' }, he: { title: '', body: '' } }

const fillChance = (text, chance) =>
  String(text || '').replace(/\{chance\}/g, String(Number(chance) || 0))

const Wrapper = styled.section`
  margin: 24px 0;
  padding: 20px;
  border: 1px solid #eceef0;
  border-radius: 4px;
  background: #ffffff;
  max-width: 720px;

  h3 {
    margin: 0 0 4px;
    font-size: 18px;
  }
  .intro {
    color: #5a6b82;
    margin: 0 0 16px;
  }
  .row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 14px;
  }
  .row label {
    min-width: 260px;
    font-weight: 600;
  }
  .row input[type='number'] {
    width: 72px;
    padding: 4px 8px;
    border: 1px solid #d9dee6;
    border-radius: 4px;
  }
  .hint {
    color: #5a6b82;
    font-size: 13px;
  }
  .preview {
    border-left: 4px solid #f2a33a;
    background: #fff7ea;
    border-radius: 4px;
    padding: 10px 12px;
    margin: 4px 0 16px;
    max-width: 420px;
  }
  .preview strong {
    display: block;
  }
  .wording {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 16px;
    margin-bottom: 16px;
  }
  .wording fieldset {
    border: 1px solid #eceef0;
    border-radius: 4px;
    padding: 10px 12px;
    min-width: 0;
  }
  .wording legend {
    font-weight: 600;
    font-size: 13px;
    padding: 0 4px;
  }
  .wording input {
    width: 100%;
    padding: 6px 8px;
    margin-bottom: 8px;
    border: 1px solid #d9dee6;
    border-radius: 4px;
  }
  .subhead {
    font-weight: 600;
    margin: 18px 0 8px;
  }
`

const CampaignStandbySettings = ({ campaignId }) => {
  const runSnackBar = useContext(SnackBarContext)
  const [loaded, setLoaded] = useState(false)
  const [enabled, setEnabled] = useState(false)
  const [percent, setPercent] = useState(DEFAULT_PERCENT)
  const [noticeEnabled, setNoticeEnabled] = useState(false)
  const [workChance, setWorkChance] = useState(DEFAULT_WORK_CHANCE)
  const [texts, setTexts] = useState(EMPTY_TEXTS)
  const [releaseHours, setReleaseHours] = useState(DEFAULT_RELEASE_HOURS)
  const [releasePercent, setReleasePercent] = useState(DEFAULT_RELEASE_PERCENT)
  const [saving, setSaving] = useState(false)

  const setText = (language, field, value) =>
    setTexts(current => ({
      ...current,
      [language]: { ...current[language], [field]: value },
    }))

  useEffect(() => {
    if (!campaignId) return
    let cancelled = false
    const load = async () => {
      try {
        const data = await readGraphQLData(
          await fetchCampaignStandbySettings({ campaignId }),
        )
        const settings = data.campaignStandbySettings
        if (cancelled || !settings) return
        const isOn =
          settings.standbyPercent !== null &&
          settings.standbyPercent !== undefined
        setEnabled(isOn)
        setPercent(isOn ? settings.standbyPercent : DEFAULT_PERCENT)
        setNoticeEnabled(!!settings.standbyNoticeEnabled)
        setWorkChance(
          settings.standbyWorkChance === null ||
            settings.standbyWorkChance === undefined
            ? DEFAULT_WORK_CHANCE
            : settings.standbyWorkChance,
        )
        const saved = settings.standbyNoticeTexts || {}
        setTexts({
          en: { ...EMPTY_TEXTS.en, ...(saved.en || {}) },
          he: { ...EMPTY_TEXTS.he, ...(saved.he || {}) },
        })
        setReleaseHours(
          settings.standbyReleaseHours === null ||
            settings.standbyReleaseHours === undefined
            ? DEFAULT_RELEASE_HOURS
            : settings.standbyReleaseHours,
        )
        setReleasePercent(
          settings.standbyReleasePercent === null ||
            settings.standbyReleasePercent === undefined
            ? DEFAULT_RELEASE_PERCENT
            : settings.standbyReleasePercent,
        )
      } catch (error) {
        runSnackBar({
          type: 'error',
          msg: 'Could not load the overbooking settings.',
        })
      } finally {
        if (!cancelled) setLoaded(true)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [campaignId]) // eslint-disable-line react-hooks/exhaustive-deps

  const onSave = async () => {
    setSaving(true)
    try {
      const data = await readGraphQLData(
        await updateCampaignStandbySettings({
          campaignId,
          standbyPercent: enabled ? Number(percent) : null,
          standbyNoticeEnabled: noticeEnabled,
          standbyWorkChance: Number(workChance),
          standbyNoticeTexts: texts,
          standbyReleaseHours: Number(releaseHours),
          standbyReleasePercent: Number(releasePercent),
        }),
      )
      const result = data.updateCampaignStandbySettings
      runSnackBar({
        type: 'success',
        msg: enabled
          ? `Saved. ${result.shifts} shifts now carry ${Number(
              percent,
            )}% standby: added ${result.created}, removed ${result.removed}.`
          : `Saved. Overbooking is off, ${
              result.removed
            } unbooked standby seats removed.`,
      })
    } catch (error) {
      runSnackBar({
        type: 'error',
        msg: (error && error.message) || 'Could not save the settings.',
      })
    } finally {
      setSaving(false)
    }
  }

  if (!loaded) return null

  return (
    <Wrapper>
      <h3>Overbooking</h3>
      <p className="intro">
        Standby BAs on top of every shift, so a no show is covered at the
        meeting point.
      </p>

      <div className="row">
        <label htmlFor="standby-enabled">Overbook this campaign</label>
        <input
          id="standby-enabled"
          type="checkbox"
          checked={enabled}
          onChange={event => setEnabled(event.target.checked)}
        />
      </div>

      {enabled && (
        <>
          <div className="row">
            <label htmlFor="standby-campaign-percent">
              Standby BAs per shift
            </label>
            <input
              id="standby-campaign-percent"
              type="number"
              min="0"
              max="100"
              step="5"
              value={percent}
              onChange={event => setPercent(event.target.value)}
            />
            <span>%</span>
            <span className="hint">
              20% gives a van of ten two standby BAs, two vans four.
            </span>
          </div>

          <div className="row">
            <label htmlFor="standby-notice">
              Tell BAs a seat is a standby seat
            </label>
            <input
              id="standby-notice"
              type="checkbox"
              checked={noticeEnabled}
              onChange={event => setNoticeEnabled(event.target.checked)}
            />
            <span className="hint">
              Off, a standby seat looks like any other mission in the app.
            </span>
          </div>

          {noticeEnabled && (
            <>
              <div className="row">
                <label htmlFor="standby-chance">Chance of working shown</label>
                <input
                  id="standby-chance"
                  type="number"
                  min="0"
                  max="100"
                  value={workChance}
                  onChange={event => setWorkChance(event.target.value)}
                />
                <span>%</span>
              </div>
              <div className="subhead">What the box says</div>
              <div className="wording">
                {LANGUAGES.map(language => (
                  <fieldset key={language.code} dir={language.dir}>
                    <legend>{language.name}</legend>
                    <input
                      id={`standby-title-${language.code}`}
                      aria-label={`${language.name} title`}
                      value={texts[language.code].title}
                      placeholder={DEFAULT_TEXTS[language.code].title}
                      maxLength={200}
                      onChange={event =>
                        setText(language.code, 'title', event.target.value)
                      }
                    />
                    <input
                      id={`standby-body-${language.code}`}
                      aria-label={`${language.name} message`}
                      value={texts[language.code].body}
                      placeholder={DEFAULT_TEXTS[language.code].body}
                      maxLength={200}
                      onChange={event =>
                        setText(language.code, 'body', event.target.value)
                      }
                    />
                    <div
                      className="preview"
                      style={{ margin: 0, maxWidth: 'none' }}
                    >
                      <strong>
                        {texts[language.code].title ||
                          DEFAULT_TEXTS[language.code].title}
                      </strong>
                      {fillChance(
                        texts[language.code].body ||
                          DEFAULT_TEXTS[language.code].body,
                        workChance,
                      )}
                    </div>
                  </fieldset>
                ))}
              </div>
              <span className="hint">
                Write {'{chance}'} where the percentage goes. Leave a line empty
                to keep the app's own wording.
              </span>
            </>
          )}

          <div className="subhead">When a standby is sent home</div>
          <div className="row">
            <label htmlFor="standby-release-hours">Hours paid (NETO)</label>
            <input
              id="standby-release-hours"
              type="number"
              min="0.25"
              max="2"
              step="0.25"
              value={releaseHours}
              onChange={event => setReleaseHours(event.target.value)}
            />
            <span className="hint">
              What the BA earns through NETO. A full shift is 2 hours.
            </span>
          </div>
          <div className="row">
            <label htmlFor="standby-release-percent">
              Share of the shift on the bill
            </label>
            <input
              id="standby-release-percent"
              type="number"
              min="0"
              max="100"
              step="5"
              value={releasePercent}
              onChange={event => setReleasePercent(event.target.value)}
            />
            <span>%</span>
            <span className="hint">
              What the payment file line charges. Applies to the next send home.
            </span>
          </div>
        </>
      )}

      <SubmitButton
        value="Save overbooking"
        size="small"
        disabled={saving}
        onClick={onSave}
      />
    </Wrapper>
  )
}

CampaignStandbySettings.propTypes = {
  campaignId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
}

export default CampaignStandbySettings
